import { CSSProperties } from "react";
import { makeAutoObservable, runInAction } from "mobx";
import { store, type RootStore } from "@/store";

const primaryButton: number = 0;
const sloppyClickThreshold: number = 5;

const calcAvatarSize = () => {
  return Math.floor(Math.max(window.innerWidth * 0.15, 120));
};

export class AvatarPositioner {
  avatarWidth = calcAvatarSize();

  avatarEl: HTMLElement | null = null;

  mouseX = 0;
  mouseY = 0;

  customAvatarPosition: null | { x: number; y: number } = null;
  isPositionedRight = false;

  dragging: null | {
    // state:
    //   "pending" means the the avatar was clicked, but we don't know if the user is dragging or not
    //   "dragging" means the user is dragging the avatar
    state: "pending" | "dragging";

    mouseX0: number;
    mouseY0: number;

    avatarX0: number;
    avatarY0: number;
  } = null;

  constructor(public rootStore: RootStore) {
    makeAutoObservable(this);

    const updateAvatarSize = () => {
      this.avatarWidth = calcAvatarSize();
    };

    window.addEventListener("resize", updateAvatarSize);

    this.loadCustomPosition();
  }

  resetPosition() {
    this.customAvatarPosition = null;
  }

  loadCustomPosition() {
    const storedPosition = sessionStorage.getItem("pyne-avatar-position");
    if (storedPosition) {
      const position = JSON.parse(storedPosition);

      runInAction(() => {
        this.customAvatarPosition = position;
      });
    }
  }

  saveCustomPosition() {
    if (this.customAvatarPosition) {
      sessionStorage.setItem("pyne-avatar-position", JSON.stringify(this.customAvatarPosition));
    } else {
      sessionStorage.removeItem("pyne-avatar-position");
    }
  }

  get avatarBottomOffset() {
    if (store.pyneSettings.debug) {
      return 44;
    }

    return 20;
  }

  get avatarPosition(): { x: number; y: number; cssPostion: CSSProperties; snapped?: boolean } {
    const defaultPosition = {
      x: 20,
      y: window.innerHeight - this.avatarBottomOffset - this.avatarWidth,
      cssPostion: { left: 20, bottom: this.avatarBottomOffset },
    };

    if (this.customAvatarPosition) {
      const snapThreshold = 20;
      const dist = Math.sqrt(
        (this.customAvatarPosition?.x - defaultPosition.x) ** 2 +
          (this.customAvatarPosition?.y - defaultPosition.y) ** 2
      );

      if (dist <= snapThreshold) {
        return { ...defaultPosition, snapped: true };
      }

      return {
        x: this.customAvatarPosition.x,
        y: this.customAvatarPosition.y,
        cssPostion: { top: this.customAvatarPosition.y, left: this.customAvatarPosition.x },
      };
    }
    if (this.isPositionedRight) {
      return {
        x: window.innerWidth - 20 - this.avatarWidth,
        y: window.innerHeight - this.avatarBottomOffset - this.avatarWidth,
        cssPostion: { right: 20, bottom: this.avatarBottomOffset },
      };
    }

    return defaultPosition;
  }

  get isDragging() {
    return this.dragging?.state === "dragging";
  }

  updateElement(el: HTMLElement | null) {
    if (this.avatarEl && this.avatarEl !== el) {
      // unregister old element
      this.avatarEl?.removeEventListener("mousedown", this.handleMouseDown);
      window.removeEventListener("click", this.handleMouseUp, { capture: true });
      window.removeEventListener("mousemove", this.handleMouseMove);
      this.avatarEl = null;
    }

    if (el && this.avatarEl !== el) {
      // register new element
      el.addEventListener("mousedown", this.handleMouseDown);
      window.addEventListener("click", this.handleMouseUp, { capture: true });
      window.addEventListener("mousemove", this.handleMouseMove);
      this.avatarEl = el;
    }
  }

  handleMouseDown = (e: MouseEvent) => {
    if (e.button !== primaryButton) return;

    this.mouseX = e.clientX;
    this.mouseY = e.clientY;
    this.dragging = {
      state: "pending",
      mouseX0: e.clientX,
      mouseY0: e.clientY,
      avatarX0: this.avatarPosition.x,
      avatarY0: this.avatarPosition.y,
    };
  };

  handleMouseMove = (e: MouseEvent) => {
    if (!this.dragging) return;

    this.mouseX = e.clientX;
    this.mouseY = e.clientY;

    const isSloppyClick =
      (!this.isDragging && Math.abs(this.dragging.mouseX0 - this.mouseX) <= sloppyClickThreshold) ||
      Math.abs(this.dragging.mouseY0 - this.mouseY) <= sloppyClickThreshold;
    if (isSloppyClick) return;

    if (!this.isDragging) {
      // drag started
      this.dragging.state = "dragging";
    }

    this.updateAvatarPosition();
  };

  handleMouseUp = (e: MouseEvent) => {
    if (this.dragging?.state === "dragging") {
      e.preventDefault();
      e.stopPropagation();

      if (this.avatarPosition.snapped) {
        this.resetPosition();
      }

      this.saveCustomPosition();
    }

    this.dragging = null;
  };

  updateAvatarPosition() {
    const avatarX = this.isDragging
      ? this.dragging!.avatarX0 + (this.mouseX - this.dragging!.mouseX0)
      : this.avatarPosition.x;
    const avatarY = this.isDragging
      ? this.dragging!.avatarY0 + (this.mouseY - this.dragging!.mouseY0)
      : this.avatarPosition.y;
    this.customAvatarPosition = { x: avatarX, y: avatarY };
  }

  get stylesheetContent() {
    let content = "";

    if (this.isDragging) {
      content += /* css */ `
        * {
          cursor: grabbing !important;
          user-select: none !important;
        }
      `;
    }

    content += /* css */ `
      #pyne-avatar, #pyne-avatar-skeleton {
        /* unset old avatar styles */
        bottom: unset;
        left: unset;
        width: unset;
        padding-bottom: unset;
        height: unset;

        position: fixed;
        --pyne-avatar-size: ${this.avatarWidth}px;
        ${this.avatarWidth === 0 ? "display: none;" : ""}
        width: ${this.avatarWidth}px;
        height: ${this.avatarWidth}px;
        ${Object.entries(this.avatarPosition.cssPostion)
          .map(([key, value]) => `${key}: ${value}px;`)
          .join("\n")}
      }
    `;
    return content;
  }
}
